// Core domain types shared between server and client. The server is authoritative
// for all of these; the client only ever renders what it receives here.

export enum GameState {
  WAITING_ROOM = "WAITING_ROOM",
  ROUND_START = "ROUND_START",
  CCC_SELECTION = "CCC_SELECTION",
  ARTIST_SELECTION = "ARTIST_SELECTION",
  WORD_SELECTION = "WORD_SELECTION",
  DRAWING = "DRAWING",
  SCORE_CALCULATION = "SCORE_CALCULATION",
  NEXT_ARTIST = "NEXT_ARTIST",
  VICTORY_CEREMONY = "VICTORY_CEREMONY",
  RETURN_TO_WAITING_ROOM = "RETURN_TO_WAITING_ROOM",
}

export type Difficulty = "easy" | "moderate" | "difficult";

export type DeepPartial<T> = T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T;

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export interface PlayerDTO {
  id: string;
  name: string;
  color: string;
  score: number;
  connected: boolean;
  isHost: boolean;
  isSpectator: boolean;
  hasGuessedCorrectly: boolean;
  guessRank: number | null;
  drawOrder: number | null;
}

// ---------------------------------------------------------------------------
// Timers
// ---------------------------------------------------------------------------

export type TimerLabel =
  | "roundStart"
  | "cccSelection"
  | "artistSelection"
  | "wordSelection"
  | "drawing"
  | "artistReconnect"
  | "scoreCalculation"
  | "nextArtist";

export interface TimerDTO {
  label: TimerLabel;
  endsAt: number;
  durationMs: number;
  paused: boolean;
  remainingMsAtPause?: number;
}

// ---------------------------------------------------------------------------
// Game snapshot (broadcast to everyone, identical for all recipients)
// ---------------------------------------------------------------------------

export interface TurnResultEntryDTO {
  playerId: string;
  points: number;
  rank: number | null;
}

export interface TurnResultDTO {
  artistId: string;
  word: string;
  difficulty: Difficulty;
  artistPoints: number;
  redrawUsed: boolean;
  guessers: TurnResultEntryDTO[];
  achievements: AchievementAwardDTO[];
}

export interface GameSnapshot {
  state: GameState;
  round: number;
  totalRounds: number;
  turnInRound: number;
  totalTurnsInRound: number;
  currentArtistId: string | null;
  timer: TimerDTO | null;
  players: PlayerDTO[];
  lastTurnResult: TurnResultDTO | null;
  /** The current artist's active Chaos Canvas Cards - only populated while state is
   * DRAWING, empty otherwise. Broadcast to everyone (not just the artist) so the
   * group understands why the canvas is behaving strangely. */
  activeArtistCCCs: CCCCardView[];
  /** Sockets watching live without a Player identity ("watch as anonymous
   * Spectator" on the join screen) - never included in `players`, just a count. */
  anonymousSpectatorCount: number;
}

// ---------------------------------------------------------------------------
// Word display (sent per-socket; masked unless recipient is privileged)
// ---------------------------------------------------------------------------

export interface WordView {
  display: string;
  length: number;
  revealed: boolean;
  difficulty: Difficulty | null;
  /** True when the artist has "Miscommunication" active and the recipient isn't
   * privileged - the client shows nothing at all (not even underscores/length). */
  hidden?: boolean;
}

export interface WordOfferOption {
  index: 0 | 1 | 2;
  difficulty: Difficulty;
  word: string;
}

export interface WordOfferDTO {
  options: WordOfferOption[];
  canRedraw: boolean;
  /** How many redraws the artist has already used this turn - the client derives
   * "X left, Y free" from this plus config.scoring.freeRedraws/maxRedraws rather
   * than the server pre-formatting a display string. */
  redrawsUsed: number;
  endsAt: number;
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export type ChatMessageType =
  | "chat"
  | "system"
  | "guess-correct"
  | "guess-close"
  | "guess-partial"
  | "announcement";

export interface ChatMessageDTO {
  id: string;
  type: ChatMessageType;
  playerId: string | null;
  playerName: string | null;
  playerColor: string | null;
  text: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Canvas / drawing
// ---------------------------------------------------------------------------

export type ToolType = "pen" | "line" | "eraser";

export interface Point {
  x: number;
  y: number;
}

export interface StrokeItem {
  kind: "stroke";
  id: string;
  tool: ToolType;
  color: string;
  size: number;
  points: Point[];
  timestamp: number;
}

export interface FillItem {
  kind: "fill";
  id: string;
  point: Point;
  color: string;
  timestamp: number;
}

export interface ClearItem {
  kind: "clear";
  id: string;
  timestamp: number;
}

export type CanvasItem = StrokeItem | FillItem | ClearItem;

export type DrawingClientEvent =
  | { type: "strokeStart"; id: string; tool: ToolType; color: string; size: number; point: Point }
  | { type: "strokePoint"; id: string; point: Point }
  /** Several consecutive strokePoints for the same stroke, batched into one
   * message (see client/ui/canvasView.ts) - `points` is flat [x0, y0, x1, y1, ...]
   * to keep the wire format small. Applies exactly like that many strokePoints. */
  | { type: "strokePoints"; id: string; points: number[] }
  | { type: "strokeEnd"; id: string }
  | { type: "fill"; id: string; point: Point; color: string }
  | { type: "clear"; id: string }
  | { type: "undo" }
  | { type: "redo" };

/** `timestamp` is omitted on strokePoint/strokePoints relays - nothing reads it
 * for those (a stroke's timing comes from its strokeStart/strokeEnd), and it was
 * the single largest field on the most frequent message the server sends. */
export type DrawingServerEvent = DrawingClientEvent & { timestamp?: number };

// ---------------------------------------------------------------------------
// Achievements
// ---------------------------------------------------------------------------

export type AchievementKey =
  | "iGotYou"
  | "picasso"
  | "isThatABird"
  | "consistency100"
  | "worthATry"
  | "perfection"
  | "speedingTicket"
  | "firstTry"
  | "purity"
  | "closeButNoCigar"
  | "quietType"
  | "photoFinish";

export interface AchievementAwardDTO {
  key: AchievementKey;
  playerId: string;
  points: number;
}

/** Bronze/silver/gold, by how an achievement's point value compares to every
 * other achievement's - see AchievementEngine.computeAchievementTiers. */
export type AchievementTier = "bronze" | "silver" | "gold";

/** An achievement as announced in the victory ceremony: shown on its own first,
 * with the players who earned it filled in afterwards (see "achievement:earned"). */
export interface AchievementShowcaseDTO {
  key: AchievementKey;
  title: string;
  description: string;
  points: number;
  tier: AchievementTier;
}

export interface VictoryRevealEntry {
  /** Which row of the standings this fills (1 = top row) - always unique. */
  rank: number;
  /** The place shown for it: players with the same score share one (1, 1, 3, 4, 4, 6). */
  place: number;
  playerId: string;
  score: number;
}

/** Purely advisory - the host remains in full control of what actually happens;
 * this just gives everyone a visible nudge. Reset at the start of every victory
 * ceremony. */
export type VictoryVoteChoice = "playAgain" | "returnToLobby";

export interface VictoryVoteCounts {
  playAgain: number;
  returnToLobby: number;
}

// ---------------------------------------------------------------------------
// Chaos Canvas Cards
// ---------------------------------------------------------------------------

/** A fully-resolved card, ready to display - names/descriptions/bonus already
 * looked up server-side so the client never needs the card catalog itself. */
export interface CCCCardView {
  familyId: string;
  level: number;
  name: string;
  description: string;
  bonusPercent: number;
}

export type CCCOfferOptionKind = "pick" | "replace" | "upgrade" | "add" | "debugPick";

export interface CCCOfferOption {
  /** Stable id echoed back in ccc:choose - the server re-validates against its own
   * cached offer rather than trusting anything else the client sends. */
  id: string;
  kind: CCCOfferOptionKind;
  /** False = grayed out (e.g. no upgrade currently possible). */
  available: boolean;
  label: string;
  card: CCCCardView | null;
  /** For "replace"/"upgrade" kinds: which of the player's current cards this affects. */
  replacesFamilyId?: string;
}

export interface CCCOfferDTO {
  situation: "A" | "B" | "C" | "DEBUG";
  options: CCCOfferOption[];
  currentInventory: CCCCardView[];
  endsAt: number;
}

export type CCCChoicePayload =
  | { choice: "skip" }
  | { choice: "discard" }
  | { choice: "select"; optionIds: string[] };

/** A synchronized fade-to-black cycle for "Blindness" - every client computes the
 * same opacity from this shared timestamp, so everyone goes blind together. */
export interface CCCBlindnessCycle {
  startAt: number;
  fadeOutMs: number;
  holdMs: number;
  fadeInMs: number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export type PlayerListOrder = "alphabetical" | "points" | "turnOrder";

/** One entry per word list the server found at startup (see
 * server/words/wordBank.ts) - pushed to clients via ServerInfoDTO so the
 * Lobby's "Word List" picker can render whatever's actually available. */
export interface WordListMeta {
  id: string;
  label: string;
}

export interface GameConfig {
  roundsPerGame: number;
  /** Hard-capped at 12 regardless of what's configured - tied to the fixed
   * 12-color player palette (see shared/palette.ts). */
  maxLobbySize: number;
  /** How the player list sidebar orders players - "turnOrder" (this round's draw
   * position, falling back to join order for anyone not yet assigned one, e.g.
   * spectators or before round 1) matches the game's pre-existing default order. */
  playerListOrder: PlayerListOrder;
  /** If false, player scores are hidden in the sidebar throughout the game (not
   * just during the pre-victory-reveal "final stretch", which always hides them
   * regardless of this setting). */
  scoresVisible: boolean;
  /** If false, joining while a game is already in progress is rejected outright
   * instead of silently becoming a spectator. */
  allowSpectators: boolean;
  /** Master switch for all 8 bonus achievements - if false, none are evaluated at
   * victory time regardless of each achievement's own `enabled` flag (see
   * "Expert: Bonus Achievements" for those individual toggles). */
  achievementsEnabled: boolean;
  /** Which loaded word list this lobby draws words from - "english" is always
   * present; anything else is whatever additional complete {prefix}_easy/
   * moderate/difficult.txt triplets were found in word_lists/ at server
   * startup (see server/words/wordBank.ts). Validated against the server's
   * actually-loaded set, not a fixed literal union, since the set is dynamic. */
  wordListId: string;
  /** If false, the masked word display (including its letter count) is hidden
   * from every non-artist, non-privileged viewer - identical to the
   * "Miscommunication" CCC's effect, but applying to every artist's turn and
   * carrying no scoring bonus. While off, "Miscommunication" itself is never
   * offered as a CCC (it would be redundant - see GameStateMachine). */
  wordHintEnabled: boolean;
  timers: {
    wordSelectionSeconds: number;
    drawingSeconds: number;
    guessTimerFloorSeconds: number;
    artistReconnectGraceSeconds: number;
    roundStartDisplaySeconds: number;
    artistSelectionDisplaySeconds: number;
    scoreCalculationDisplaySeconds: number;
    nextArtistDisplaySeconds: number;
    victoryRevealDelaySeconds: number;
    /** Victory ceremony pacing, in order: wait after the ceremony starts before
     * the first achievement, wait after an achievement appears before the
     * players who earned it do, gap between each earner's dot appearing, and
     * wait after the last dot before the next achievement. */
    achievementStartDelaySeconds: number;
    achievementPlayerRevealDelaySeconds: number;
    achievementDotIntervalSeconds: number;
    achievementNextDelaySeconds: number;
    disconnectRemovalSeconds: number;
  };
  scoring: {
    artistFirstGuess: number;
    artistAdditionalGuess: number;
    playerFirst: number;
    playerSecond: number;
    playerThird: number;
    playerRemaining: number;
    decayAmount: number;
    decayIntervalSeconds: number;
    /** Whether the artist can re-roll their offered words at all this turn. */
    redrawEnabled: boolean;
    redrawPenalty: number;
    /** Re-rolls per turn that don't cost redrawPenalty - the first N are free. */
    freeRedraws: number;
    /** Total re-rolls allowed per turn, free ones included. */
    maxRedraws: number;
  };
  difficultyMultipliers: Record<Difficulty, number>;
  achievements: Record<AchievementKey, { enabled: boolean; points: number }>;
  chat: {
    rateLimitMessagesPerWindow: number;
    rateLimitWindowMs: number;
    historyLimit: number;
    guessCloseThreshold: number;
    inactivityWipeMinutes: number;
    wordFilter: {
      enabled: boolean;
      /** Extra words/phrases to mask, on top of the built-in base list. */
      customWords: string[];
    };
  };
  hints: {
    enabled: boolean;
    intervalSeconds: number;
    /** Hints stop once this percentage of the word's maskable characters have
     * been revealed (floored), regardless of how long the turn runs. */
    maxRevealPercent: number;
  };
  ccc: {
    schedule: CCCSchedule;
    /** If false (default), level 2+ cards can only be reached by upgrading - they
     * never appear in a direct-pickup pool (the 3-card offer, "replace", "add"). */
    allowDirectHighLevelPickup: boolean;
    /** Admin-only testing aid: shows every card at every level and allows picking
     * up to 2 directly, bypassing normal replace/upgrade rules. */
    debugMode: boolean;
    selectionSeconds: number;
    /** Host-disabled cards, as "familyId:level" pairs (e.g. "tremble:1") - never
     * offered via any path (direct pick, add, replace, upgrade, or debug mode).
     * Doesn't retroactively strip a card a player already picked up before the
     * host disabled it. */
    disabledCards: string[];
  };
}

export type CCCSchedule =
  | "off"
  | "startOnly"
  | "startAndEveryRound"
  | "startAndEveryOtherRound"
  | "everyRound"
  | "everyOtherRound";

// ---------------------------------------------------------------------------
// Join / reconnect
// ---------------------------------------------------------------------------

export interface JoinRequestDTO {
  name: string;
  color: string;
}

export interface JoinAckDTO {
  ok: boolean;
  reason?: string;
  playerId?: string;
  reconnectToken?: string;
  isSpectator?: boolean;
}

export interface ReconnectRequestDTO {
  playerId: string;
  reconnectToken: string;
}

export interface ReconnectAckDTO {
  ok: boolean;
  reason?: string;
  isSpectator?: boolean;
}

export interface ReclaimRequestDTO {
  name: string;
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

/** One finished drawing in the end-of-game gallery. The image itself is a PNG the
 * server saved when the turn ended, fetched from `imageUrl`. */
export interface GalleryEntryDTO {
  id: string;
  round: number;
  turn: number;
  artistName: string;
  artistColor: string;
  word: string;
  /** What the artist earned for this drawing. */
  points: number;
  /** Names of the Chaos Canvas Cards that were active while they drew it. */
  cccNames: string[];
  imageUrl: string;
}

// ---------------------------------------------------------------------------
// Lobbies (the code-having, URL-addressable room a game runs inside - not to be
// confused with GameState.WAITING_ROOM, the pre-game screen within a lobby)
// ---------------------------------------------------------------------------

/** Response shape for both `POST /api/lobbies` and `GET /api/lobbies/:code` - the
 * server's LobbyManager is the single source of truth both endpoints read from. */
export interface LobbyInfoDTO {
  code: string;
  exists: boolean;
  playerCount: number;
  maxPlayers: number;
  state: GameState | null;
}
